import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import styles from "../../../styles/components/Modal/Payments/EditPaymentsModal.module.css";
import CloseIcon from "@mui/icons-material/Close";
import { fetchPaymentById } from '../../../utils/api/Lead/Payments/fetchPaymentById';
import { updatePayment } from '../../../utils/api/Lead/Payments/updatePayment';
import { deletePayment } from '../../../utils/api/Lead/Payments/deletePayment';
import { LoadingCircle } from '../../UI/Custom/LoadingCircle';
import { InputSelector } from '../../UI/InputSelector';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { Notification } from '../../UI/Notifications';
import { handleError } from '../../../utils/custom/errorHandler';
import { paymentFormFields, getPaymentFormLabels } from '../../../utils/forms/paymentFormFields';
import { formatDateTime } from '../../../utils/custom/formatters';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { getTransactionActionLabel } from '../../../utils/transactionActionLabels';

const toDateTimeLocalValue = (value) => {
    if (!value) return '';

    const apiDate = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/.exec(value);
    if (apiDate) {
        return `${apiDate[3]}-${apiDate[2]}-${apiDate[1]}T${apiDate[4]}:${apiDate[5]}`;
    }

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
    return isoDate
        ? `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}T${isoDate[4]}:${isoDate[5]}`
        : '';
};

export const EditPaymentModal = ({ leadId, paymentId, onClose, onPaymentActionSuccess }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = getPaymentFormLabels(langSelected);

    const modalRef = useRef(null);
    const [paymentData, setPaymentData] = useState(null);
    const [initialPaymentData, setInitialPaymentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [hoveredInfo, setHoveredInfo] = useState(null);
    const [infoModalPosition, setInfoModalPosition] = useState({ x: 0, y: 0 });

    const formFields = useMemo(() => paymentFormFields, []);

    const hasChanges = useMemo(() => {
        if (!initialPaymentData || !paymentData) return false;
        const editableFields = formFields.filter(field => field.editable).map(field => field.key);

        for (const key of editableFields) {
            if (formFields.find(f => f.key === key)?.dataType === 'number') {
                if (parseFloat(paymentData[key]) !== parseFloat(initialPaymentData[key])) {
                    return true;
                }
            } else {
                const currentVal = paymentData[key] !== null ? String(paymentData[key]) : null;
                const initialVal = initialPaymentData[key] !== null ? String(initialPaymentData[key]) : null;
                if (currentVal !== initialVal) {
                    return true;
                }
            }
        }
        return false;
    }, [paymentData, initialPaymentData, formFields]);

    const inputFieldsConfig = formFields.filter((field) => field.type === "input" && field.editable);
    const dropdownFieldsConfig = formFields.filter((field) => field.type === "dropdown" && field.editable);
    const nonEditableFieldsConfig = formFields.filter((field) => !field.editable);

    const loadPayment = useCallback(async () => {
        setLoading(true);
        setErrors({});
        setTouched({});
        try {
            const data = await fetchPaymentById(leadId, paymentId);
            const normalizedData = {
                ...data,
                date: toDateTimeLocalValue(data.date),
            };
            setPaymentData(normalizedData);
            setInitialPaymentData(normalizedData);
        } catch (err) {
            handleError(err.response?.data?.message || err.message || err, setErrors, langSelected);
        } finally {
            setLoading(false);
        }
    }, [leadId, paymentId, langSelected]);

    useEffect(() => {
        loadPayment();
    }, [loadPayment]);

    const handleChange = useCallback((key, value) => {
        setPaymentData(prevData => {
            const fieldConfig = formFields.find(f => f.key === key);
            let parsedValue = value;

            if (value === '') {
                parsedValue = null;
            } else if (fieldConfig && fieldConfig.dataType === 'number') {
                parsedValue = parseFloat(value);
                if (isNaN(parsedValue)) {
                    parsedValue = value;
                }
            }
            return { ...prevData, [key]: parsedValue };
        });

        setTouched((prev) => ({ ...prev, [key]: true }));

        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[key]) {
                delete newErrors[key];
            }
            if (newErrors.general) {
                delete newErrors.general;
            }
            return newErrors;
        });
    }, [formFields]);

    const handleFocus = useCallback((key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[key]) {
                delete newErrors[key];
            }
            if (newErrors.general) {
                delete newErrors.general;
            }
            return newErrors;
        });
    }, []);

    const handleBlur = useCallback((key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    }, []);

    const handleSave = async () => {
        setErrors({});
        setIsSaving(true);
        try {
            const changes = {};
            const editableFields = formFields.filter(field => field.editable);

            for (const field of editableFields) {
                const key = field.key;
                let currentVal = paymentData[key] !== null ? String(paymentData[key]) : null;
                let initialVal = initialPaymentData[key] !== null ? String(initialPaymentData[key]) : null;

                if (field.dataType === 'number') {
                    currentVal = parseFloat(currentVal);
                    initialVal = parseFloat(initialVal);
                }

                if (currentVal !== initialVal) {
                    changes[key] = paymentData[key];
                }
            }

            if (Object.keys(changes).length > 0) {
                await updatePayment(leadId, paymentId, changes);
                onPaymentActionSuccess();
                onClose();
            } else {
                onClose();
            }
        } catch (err) {
            handleError(err.response?.data?.message || err.message || err, setErrors, langSelected);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = () => setIsConfirmingDelete(true);

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        setErrors({});
        try {
            await deletePayment(paymentId);
            onPaymentActionSuccess();
            onClose();
        } catch (err) {
            handleError(err.response?.data?.message || err.message || err, setErrors, langSelected);
        } finally {
            setIsDeleting(false);
            setIsConfirmingDelete(false);
        }
    };

    const handleCancelDelete = () => setIsConfirmingDelete(false);

    const handleMouseEnterInfo = useCallback((event, data, type) => {
        let displayData = {};
        if (type === 'admin') {
            displayData = {
                [t.infoModal.id]: data.id,
                [t.infoModal.firstName]: data.first_name || '-',
                [t.infoModal.lastName]: data.last_name || '-',
                [t.infoModal.email]: data.email,
                [t.infoModal.role]: data.role?.name || '-',
                [t.infoModal.status]: data.status,
                [t.infoModal.createdAt]: formatDateTime(data.created_at),
            };
        } else if (type === 'date') {
            displayData = { [t.infoModal.dateAndTime]: formatDateTime(data) };
        }
        setHoveredInfo({ data: displayData, type: type });
        setInfoModalPosition({ x: event.clientX - 100, y: event.clientY + 15 });
    }, [t.infoModal]);

    const handleMouseLeaveInfo = useCallback(() => {
        setHoveredInfo(null);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const modalClass = `${styles.modal} ${theme === 'dark' ? styles.darkModal : ''}`;
    const filtersContainerClass = `${styles.filtersContainer} ${theme === 'dark' ? styles.darkFiltersContainer : ''}`;
    const modalLabelClass = `${styles.modalLabel} ${theme === 'dark' ? styles.darkModalLabel : ''}`;
    const modalContentClass = `${styles.modalContent} ${theme === 'dark' ? styles.darkModalContent : ''}`;
    const inputRowLabelClass = `${styles.inputRow} ${styles.label} ${theme === 'dark' ? styles.darkInputRowLabel : ''}`;
    const modalFooterClass = `${styles.modalFooter} ${theme === 'dark' ? styles.darkModalFooter : ''}`;
    const submitButtonClass = `${styles.submitButton} ${theme === 'dark' ? styles.darkSubmitButton : ''}`;
    const deleteButtonClass = `${styles.deleteButton} ${theme === 'dark' ? styles.darkDeleteButton : ''}`;
    const confirmButtonClass = `${styles.confirmButton} ${theme === 'dark' ? styles.darkConfirmButton : ''}`;
    const cancelButtonClass = `${styles.cancelButton} ${theme === 'dark' ? styles.darkCancelButton : ''}`;
    const confirmTextClass = `${styles.confirmText} ${theme === 'dark' ? styles.darkConfirmText : ''}`;
    const hoverInfoModalClass = `${styles.hoverInfoModal} ${theme === 'dark' ? styles.darkHoverInfoModal : ''}`;
    const formInputClass = `${styles.formInput} ${theme === 'dark' ? styles.darkFormInput : ''}`;


    if (!paymentData && !loading && Object.keys(errors).length > 0) {
        return (
            <div className={modalClass}>
                <div className={filtersContainerClass}>
                    <p className={styles.error}>{errors.general || t.errorLoading}</p>
                    <button className={cancelButtonClass} onClick={onClose}>{t.close}</button>
                </div>
            </div>
        );
    }


    const isSaveButtonDisabled = !hasChanges || isSaving || loading || isDeleting || isConfirmingDelete;

    return (
        <div className={modalClass}>
            {errors.general && <Notification message={errors.general} type={"error"} />}
            <div className={filtersContainerClass} ref={modalRef}>
                <div className={modalLabelClass}>
                    <p>{t.editPaymentTitle(paymentId)}</p>
                    <div onClick={onClose} className={theme === 'dark' ? styles.darkCloseIcon : ''}>
                        <CloseIcon />
                    </div>
                </div>

                {loading ? (
                    <div className={styles.loadingContainer}>
                        <LoadingCircle size={30} thickness={1} />
                    </div>
                ) : (
                    <div className={modalContentClass}>
                        {dropdownFieldsConfig.map((field) => (
                            <div
                                key={field.key}
                                className={styles.inputRow}
                                style={{ gridColumn: 'span 6' }}
                            >
                                <label className={inputRowLabelClass}>{t[field.label]}:</label>
                                <DropdownSelector
                                    filterName={field.key}
                                    selectedValue={paymentData?.[field.key] || ''}
                                    handleFilterChange={handleChange}
                                    options={field.options.map(option => ({
                                        ...option,
                                        label: field.key === 'action'
                                            ? getTransactionActionLabel(option.value, langSelected)
                                            : (t[option.label] || option.label)
                                    }))}
                                    onFocus={() => handleFocus(field.key)}
                                    onBlur={() => handleBlur(field.key)}
                                    hasError={!!errors[field.key] && !!touched[field.key]}
                                    errorMessage={errors[field.key]}
                                    isTouched={!!touched[field.key]}
                                    theme={theme}
                                />
                            </div>
                        ))}

                        {inputFieldsConfig.map((field) => (
                            <div
                                key={field.key}
                                className={styles.inputRow}
                                style={{ gridColumn: 'span 6' }}
                            >
                                <label className={inputRowLabelClass}>{t[field.label]}:</label>
                                <InputSelector
                                    filterName={field.key}
                                    selectedValue={paymentData?.[field.key] === null ? '' : paymentData?.[field.key]}
                                    handleFilterChange={handleChange}
                                    onFocus={() => handleFocus(field.key)}
                                    onBlur={() => handleBlur(field.key)}
                                    hasError={!!errors[field.key] && !!touched[field.key]}
                                    errorMessage={errors[field.key]}
                                    type={field.dataType || 'text'}
                                    isTouched={!!touched[field.key]}
                                    placeholder={field.placeholder}
                                    theme={theme}
                                />
                            </div>
                        ))}

                        {nonEditableFieldsConfig.map((field) => (
                            <div key={field.key} className={styles.inputRow} style={{ gridColumn: 'span 6' }}>
                                <label className={inputRowLabelClass}>{t[field.label]}:</label>
                                <input
                                    type="text"
                                    value={
                                        field.key === 'assets'
                                            ? paymentData?.assets || '-'
                                            : (['date', 'created_at', 'updated_at'].includes(field.key)
                                                ? formatDateTime(paymentData?.[field.key])
                                                : (field.key === 'created_by_admin'
                                                    ? (paymentData?.created_by_admin ? `${paymentData.created_by_admin.email}` : '-')
                                                    : (paymentData?.[field.key] !== null ? paymentData?.[field.key] : '-')
                                                )
                                            )
                                    }
                                    className={formInputClass}
                                    readOnly
                                    onMouseEnter={(e) => {
                                        if (field.key === 'created_by_admin' && paymentData?.created_by_admin) {
                                            handleMouseEnterInfo(e, paymentData.created_by_admin, 'admin');
                                        } else if (['date', 'created_at', 'updated_at'].includes(field.key) && paymentData?.[field.key]) {
                                            handleMouseEnterInfo(e, paymentData[field.key], 'date');
                                        }
                                    }}
                                    onMouseLeave={handleMouseLeaveInfo}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <div className={modalFooterClass}>
                    <div className={styles.confirmationWrapper}>
                        {!isConfirmingDelete && (
                            <button
                                type="button"
                                className={submitButtonClass}
                                onClick={handleSave}
                                disabled={isSaveButtonDisabled}
                            >
                                {isSaving ? t.saving : t.saveChanges}
                            </button>
                        )}

                        {!isConfirmingDelete ? (
                            <button
                                type="button"
                                className={deleteButtonClass}
                                onClick={handleDeleteClick}
                                disabled={isSaving || isDeleting || loading}
                            >
                                {t.deletePayment}
                            </button>
                        ) : (
                            <>
                                <p className={confirmTextClass}>{t.areYouSure}</p>
                                <button
                                    type="button"
                                    className={confirmButtonClass}
                                    onClick={handleConfirmDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? t.deleting : t.yesDelete}
                                </button>
                                <button
                                    type="button"
                                    className={cancelButtonClass}
                                    onClick={handleCancelDelete}
                                    disabled={isDeleting}
                                >
                                    {t.noCancel}
                                </button>
                            </>
                        )}

                        {!isConfirmingDelete && (
                            <button
                                type="button"
                                className={cancelButtonClass}
                                onClick={onClose}
                                disabled={isSaving || isDeleting || loading}
                            >
                                {t.cancel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {hoveredInfo && (
                <div
                    className={hoverInfoModalClass}
                    style={{ left: infoModalPosition.x, top: infoModalPosition.y }}
                >
                    {Object.entries(hoveredInfo.data).map(([key, value]) => (
                        <p key={key}><strong>{key}:</strong> {value}</p>
                    ))}
                </div>
            )}
        </div>
    );
};
